import { useTranslation } from 'react-i18next'
import { mdiArchiveOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'

/**
 * Marks something that points at an archived asset: the archive pictogram plus
 * the localized "Archived" label, in the muted colour. Generic on purpose, it
 * carries no asset knowledge, so any surface that names an archived asset (an
 * asset page, a contract's covered assets, a document's "filed on" line) uses
 * the same one.
 *
 * Renders nothing when `archivedAt` is falsy, so a caller can pass the field
 * straight through.
 */
export function ArchivedMarker({
    archivedAt,
    testId = 'asset-archived-marker',
    className = '',
}: {
    archivedAt?: string | null
    testId?: string
    className?: string
}) {
    const { t } = useTranslation('common')
    if (!archivedAt) return null
    return (
        <span
            className={`inline-flex items-center gap-1 whitespace-nowrap text-xs font-normal text-muted-foreground ${className}`}
            data-testid={testId}
        >
            <AppIcon path={mdiArchiveOutline} size="xs" />
            {t('archived_marker.label')}
        </span>
    )
}

/**
 * An asset's name, struck through and followed by the `ArchivedMarker` when
 * `archivedAt` is set. A live asset renders as the bare name.
 */
export function AssetName({
    name,
    archivedAt,
    className = '',
}: {
    name: string
    archivedAt?: string | null
    className?: string
}) {
    if (!archivedAt) return <span className={className}>{name}</span>
    return (
        <span className={`inline-flex items-center gap-1.5 ${className}`}>
            <span className="line-through" data-testid="asset-name-archived">
                {name}
            </span>
            <ArchivedMarker archivedAt={archivedAt} />
        </span>
    )
}
